import React, { useState } from "react";

import PlaceholderApp from "../../../components/Placeholders/App";

import useErrorExternalStore from "../../hooks/useErrorExternalStore";
import { Causes, HttpError } from "../../types";
import ErrorModal from "./ErrorModal";
import InvalidSession from "./InvalidSession";

interface WithErrorHandlerProps {
    children?: React.ReactNode;
}

const getErrorComponent = (
    error: HttpError | undefined,
    dismissError: () => void,
) => {
    if (error?.cause === Causes.FetchException) {
        return (
            <ErrorModal
                namespace="http_fetch_error"
                error={error}
                dismissError={dismissError}
            />
        );
    }
    if (error?.cause === Causes.HandlerRaisedException) {
        return (
            <ErrorModal
                namespace="http_handler_error"
                error={error}
                dismissError={dismissError}
            />
        );
    }
    if (error?.cause === Causes.AccessTokenExpired) {
        return <InvalidSession />;
    }
    switch (error?.status) {
        case 401: {
            return <InvalidSession />;
        }
        case 403: {
            return (
                <ErrorModal
                    namespace="http_403_error"
                    error={error}
                    dismissError={dismissError}
                />
            );
        }
        case 500: {
            return (
                <ErrorModal
                    namespace="http_500_error"
                    error={error}
                    dismissError={dismissError}
                />
            );
        }
        case 502: {
            return (
                <ErrorModal
                    namespace="http_502_error"
                    error={error}
                    dismissError={dismissError}
                />
            );
        }
        case 503: {
            return (
                <ErrorModal
                    namespace="http_503_error"
                    error={error}
                    dismissError={dismissError}
                />
            );
        }
        case 504: {
            return (
                <ErrorModal
                    namespace="http_504_error"
                    error={error}
                    dismissError={dismissError}
                />
            );
        }
        default: {
            return (
                <ErrorModal
                    namespace="http_handler_error"
                    error={error}
                    dismissError={dismissError}
                />
            );
        }
    }
};

const WithErrorHandler = ({ children }: WithErrorHandlerProps) => {
    const error = useErrorExternalStore();
    // Every dispatch into errorExternalStore builds a brand-new HttpError
    // object (see api.ts's handleError), so comparing by reference — rather
    // than by content — reliably distinguishes "the same dismissed error,
    // still showing" from "a new error occurred, even if it looks the same"
    // (e.g. retrying the same failing submit twice in a row).
    const [dismissedError, setDismissedError] = useState<HttpError | undefined>(
        undefined,
    );

    // We keep the dismissed error out of the redux store to avoid re-rendering
    // the view that prompted the error.
    const dismissError = () => {
        if (error?.hasError) {
            setDismissedError(error.httpError);
        }
    };

    if (error?.hasError && error.httpError !== dismissedError) {
        // The application has an unhandled error when fetching data.

        const ErrorComponent = getErrorComponent(error.httpError, dismissError);
        if (
            error.httpError?.status === 401 ||
            error.httpError?.cause === Causes.AccessTokenExpired
        ) {
            // If the session is invalid do not leak sensitive data, hide actual child content and show the placeholder instead.
            return (
                <>
                    <PlaceholderApp />
                    {ErrorComponent}
                </>
            );
        }
        // Show application with current state and an error message.
        return (
            <>
                {children}
                {ErrorComponent}
            </>
        );
    }
    return <>{children}</>;
};

export default WithErrorHandler;
